/*
  Form creator component
*/

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Grid from '@mui/material/Grid'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { useForm } from 'react-hook-form'

import { yupResolver } from '@hookform/resolvers/yup'
import get from 'lodash/get'
import { forwardRef, useEffect, useImperativeHandle } from 'react'
import { useTranslation } from 'react-i18next'
import * as Yup from 'yup'
import FormItem from './FormItem'

// Generate schema for Yup resolved based on form items
const GenerateSchema = (formItems = []) => {
  const validations = {}

  formItems.forEach(item => {
    const { name, validator } = item

    if (validator) {
      validations[name] = validator
    }
  })

  return Yup.object().shape(validations)
}

// Render a form with given form items
// formItems format
// {
//   label: String (input label),
//   placeholder: String (input placeholder),
//   name: String (input name),
//   defaultValue: String (input default values),
//   type: String (input component type),
//   component: JSX.element (custom component),
//   validator: YupSchema (e.g. Yup.string().required()),
// }
const FormCreator = (
  {
    formItems = [],
    // actionItems = [],
    loading = false,
    errors,
    extraComponents = {},
    formConfig,
    gridItemProps,
    submitComponent,
    submitProps,
    reset: propsReset,
    resetComponent,
    resetProps,
    submit = () => {},
    submitLabel,
    resetLabel,
    flexDirection = 'column',
    onValidChange,
    sx,
    isCreateRegisteredUserCard = false,
  },
  ref,
) => {
  const form = useForm({
    mode: 'all',
    resolver: yupResolver(GenerateSchema(formItems)),
    ...formConfig,
  })

  useImperativeHandle(ref, () => form)

  const { t } = useTranslation()

  const { control, handleSubmit, reset, formState } = form
  const { preSubmit, postSubmit } = extraComponents

  // eslint-disable-next-line no-unused-vars
  const { isValid, errors: formErrors } = formState

  Yup.addMethod(Yup.string, 'email', function validateEmail(message) {
    return this.matches(
      // eslint-disable-next-line no-useless-escape
      /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
      {
        message,
        name: 'email',
        excludeEmptyString: true,
      },
    )
  })

  useEffect(() => {
    if (isValid) {
      onValidChange?.(isValid)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isValid])

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(submit)}
      display="flex"
      flexDirection={flexDirection}
      sx={{
        '& .MuiTypography-root': { fontSize: 14 },
        '& .MuiInputBase-root': {
          fontSize: 14,
          padding: 1,
          '& .MuiInputBase-input': { padding: 0 },
        },
        ...sx,
      }}
    >
      <Grid
        direction={flexDirection}
        container
        spacing={2}
        flex={1}
        {...(flexDirection === 'row' ? { alignItems: 'center' } : {})}
      >
        {formItems &&
          formItems.map((data, index) => {
            const { gridProps, ...d } = data

            let md = 'auto'
            if (isCreateRegisteredUserCard) {
              md = index === 1 ? 4.8 : 2.4
            }
            return (
              <Grid item key={`formItem_${data.name}`} md={md} {...gridItemProps} {...gridProps}>
                <FormItem {...d} control={control} errors={formErrors} />
              </Grid>
            )
          })}
      </Grid>

      {get(errors, 'form') && (
        <Typography sx={{ m: 1 }} color="error.main" variant="caption" alignSelf="flex-end">
          {get(errors, 'form.message')}
        </Typography>
      )}
      {/* {actionItems &&
        actionItems.map((data, index) => (
          <div key={`formAction_${data.name}_${index}`} onSubmit={onSubmit} {...data} />
        ))} */}

      {preSubmit?.(form)}

      <Stack direction={'row'} spacing={2} mt={3} alignItems="center" justifyContent="center">
        {resetComponent ? (
          resetComponent()
        ) : (
          <Button
            onClick={() => {
              if (propsReset) {
                propsReset(reset)
              } else {
                reset()
              }
            }}
            variant="contained"
            fullWidth
            disableElevation
            {...resetProps}
          >
            {resetLabel || t('general.reset')}
          </Button>
        )}
        {/* {submitComponent ? (
          submitComponent()
        ) : (
          <Button
            type="submit"
            variant="contained"
            fullWidth
            disableElevation
            disabled={!isValid}
            {...submitProps}
            // disabled={!isValid}
          >
            {loading && <CircularProgress color="neutral" size={18} sx={{ mr: 1 }} />}
            {submitLabel || t('general.submit')}
          </Button>
        )} */}
        <div inert={loading ? 'true' : undefined}>
          {' '}
          {/* 根據條件傳遞屬性 */}
          {submitComponent ? (
            submitComponent()
          ) : (
            <Button
              type="submit"
              variant="contained"
              fullWidth
              disableElevation
              disabled={!isValid || loading} // Disable if not valid or loading
              {...submitProps}
            >
              {loading && <CircularProgress color="neutral" size={18} sx={{ mr: 1 }} />}
              {submitLabel || t('general.submit')}
            </Button>
          )}
        </div>
      </Stack>
      {postSubmit?.(form)}
    </Box>
  )
}

export default forwardRef(FormCreator)
